import { getGearSnapPoints } from './gear'
import { GearElement, GROUND_Y, Point, RopeContact, RopeElement, RopeEnd, RopeHandleDrag, SnapCandidate, SnapRole, SNAP_DISTANCE, SNAP_DROP_DISTANCE } from './types'

export function resolveRopePoint(end: RopeEnd, gears: GearElement[]): Point {
  if (end.mode === 'free') return { x: end.x, y: end.y }
  const gear = gears.find((item) => item.id === end.elementId)
  if (!gear) return { x: 0, y: 0 }
  const snap = getGearSnapPoints(gear).find((point) => point.id === end.snapPointId)
  return snap?.world ?? { x: gear.x + gear.width / 2, y: gear.y + gear.height / 2 }
}

export function resolveContactPoint(contact: RopeContact, gears: GearElement[]): Point {
  const gear = gears.find((item) => item.id === contact.elementId)
  if (!gear) return { x: 0, y: 0 }
  const snap = getGearSnapPoints(gear).find((point) => point.id === contact.snapPointId)
  return snap?.world ?? { x: gear.x + gear.width / 2, y: gear.y + gear.height / 2 }
}

export function getRopeControlPoints(rope: RopeElement, gears: GearElement[]): Point[] {
  return [
    resolveRopePoint(rope.start, gears),
    ...rope.contacts.map((contact) => resolveContactPoint(contact, gears)),
    resolveRopePoint(rope.end, gears),
  ]
}

export type RopePathOptions = {
  groundY?: number
  contactTypes?: Array<'pulley' | 'deviation' | 'connector' | 'tail' | 'path' | null>
  sagMultiplier?: number
}

// Quadratic control-point Y for a sagging segment. A rope sags downward, so the
// control point must sit at or below the lower endpoint — never above it. The
// ground line only holds the belly up for ropes that are above it; segments that
// already sit below the ground line (e.g. a load in a pit) just sag normally.
function sagControlY(prev: Point, curr: Point, sag: number, groundY: number): number {
  const lower = Math.max(prev.y, curr.y)
  const belly = lower + sag
  return lower < groundY - 24 ? Math.min(belly, groundY - 24) : belly
}

export function getRopePath(
  points: Point[],
  groundY?: number,
  contactTypes?: Array<'pulley' | 'deviation' | 'connector' | 'tail' | 'path' | null>,
  sagMultiplier?: number,
  autoSag?: boolean,
): string {
  const gy = groundY ?? GROUND_Y
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`

  const sm = sagMultiplier ?? 1

  const commands = [`M ${points[0].x} ${points[0].y}`]
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    const dx = curr.x - prev.x
    const dy = curr.y - prev.y
    const distance = Math.hypot(dx, dy)

    const prevType = contactTypes?.[i - 1] ?? null
    const currType = contactTypes?.[i] ?? null
    const isTail = prevType === 'tail' || currType === 'tail'

    if (isTail) {
      const cpX = prev.x + dx * 0.4
      const cpY = prev.y + (curr.y - prev.y) * 0.6
      commands.push(`Q ${cpX} ${cpY} ${curr.x} ${curr.y}`)
      continue
    }

    // Straight line to contact points (visible redirect at gear) and to free
    // ends drawn as a taut path. A free end shows the route the rope takes —
    // not where it would physically hang — so its leg is straight, not sagged.
    if (currType || prevType === 'path') {
      commands.push(`L ${curr.x} ${curr.y}`)
      continue
    }

    // Auto-sag: belly = half the horizontal span (re-anchor mode)
    if (autoSag) {
      const autoSagAmount = Math.max(10, Math.abs(dx) / 2)
      const controlX = prev.x + dx * 0.5
      commands.push(`Q ${controlX} ${sagControlY(prev, curr, autoSagAmount, gy)} ${curr.x} ${curr.y}`)
      continue
    }

    const sag = Math.min(90, Math.max(18 * sm, distance * 0.16 * sm))

    const weightFactor = dy / (2 * Math.max(distance, 1))
    const sagT = 0.5 + Math.max(-0.35, Math.min(0.35, weightFactor * 0.6))

    const controlX = prev.x + dx * sagT
    commands.push(`Q ${controlX} ${sagControlY(prev, curr, sag, gy)} ${curr.x} ${curr.y}`)
  }
  return commands.join(' ')
}

export function findNearestSnap(point: Point, gears: GearElement[], maxDistance = SNAP_DISTANCE): SnapCandidate | null {
  let best: SnapCandidate | null = null

  for (const gear of gears) {
    const snapPoints = getGearSnapPoints(gear)
    for (const snap of snapPoints) {
      const distance = Math.hypot(snap.world.x - point.x, snap.world.y - point.y)
      if (distance > maxDistance) continue
      if (!best || distance < best.distance) {
        best = {
          elementId: gear.id,
          snapPointId: snap.id,
          point: snap.world,
          role: snap.role,
          distance,
        }
      }
    }
  }

  return best
}

// Snap roles that represent physical attachment points (rope termination, gear
// connection, harness clip-in). Excludes roles that represent rope *pass-through*
// points (sheave, deviation contact) and the decorative center marker.
const CONNECTABLE_ROLES: ReadonlySet<SnapRole> = new Set([
  'connector', 'load', 'anchor', 'harness',
])

export type GearSnapMatch = {
  sourceSnapId: string
  target: SnapCandidate
}

/**
 * For a dragged gear, find the nearest snap-point on a *different* gear whose
 * role is connectable (anchor/connector/load/harness). Returns the source snap
 * id on the dragged gear together with a SnapCandidate for the target gear so
 * the caller can create a rope between them.
 *
 * `excludeIds` lets the caller skip co-selected gears during multi-drag so
 * they don't accidentally snap to each other.
 */
export function findNearestGearSnap(
  draggedGear: GearElement,
  allGears: GearElement[],
  maxDistance = SNAP_DROP_DISTANCE,
  excludeIds?: ReadonlySet<string>,
): GearSnapMatch | null {
  const srcSnaps = getGearSnapPoints(draggedGear)
  let best: GearSnapMatch | null = null
  let bestDist = maxDistance

  for (const src of srcSnaps) {
    if (!CONNECTABLE_ROLES.has(src.role)) continue
    for (const other of allGears) {
      if (other.id === draggedGear.id) continue
      if (excludeIds?.has(other.id)) continue
      const otherSnaps = getGearSnapPoints(other)
      for (const tgt of otherSnaps) {
        if (!CONNECTABLE_ROLES.has(tgt.role)) continue
        const d = Math.hypot(src.world.x - tgt.world.x, src.world.y - tgt.world.y)
        if (d < bestDist) {
          bestDist = d
          best = {
            sourceSnapId: src.id,
            target: {
              elementId: other.id,
              snapPointId: tgt.id,
              point: tgt.world,
              role: tgt.role,
              distance: d,
            },
          }
        }
      }
    }
  }

  return best
}

export function snapCandidateToRopeEnd(candidate: SnapCandidate | null, point: Point): RopeEnd {
  // Free ends default to falling to the ground — ropes need to be attached to
  // something or they hang to the floor.
  if (!candidate) return { mode: 'free', x: point.x, y: point.y, fallsToGround: true }
  return { mode: 'attached', elementId: candidate.elementId, snapPointId: candidate.snapPointId }
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2))
  const cx = a.x + t * dx
  const cy = a.y + t * dy
  return Math.hypot(p.x - cx, p.y - cy)
}

export function findBestSegmentIndex(points: Point[], target: Point): number {
  if (points.length < 2) return 0
  let best = 0
  let bestDist = Infinity
  for (let i = 0; i < points.length - 1; i++) {
    const d = distanceToSegment(target, points[i], points[i + 1])
    if (d < bestDist) {
      bestDist = d
      best = i
    }
  }
  return best
}

export function getRopeControlPointsWithDrag(
  rope: RopeElement,
  gears: GearElement[],
  drag: RopeHandleDrag | null,
): Point[] {
  const points = getRopeControlPoints(rope, gears)
  if (!drag || drag.ropeId !== rope.id) return points
  if (drag.ref.kind === 'endpoint') {
    const idx = drag.ref.end === 'start' ? 0 : points.length - 1
    const next = points.slice()
    next[idx] = drag.position
    return next
  }
  if (drag.ref.kind === 'contact') {
    const idx = drag.ref.index + 1
    if (idx <= 0 || idx >= points.length - 1) return points
    const next = points.slice()
    next[idx] = drag.position
    return next
  }
  // midpoint: insert ghost between afterIndex and afterIndex + 1
  const insertAt = drag.ref.afterIndex + 1
  if (insertAt <= 0 || insertAt > points.length - 1) return points
  return [...points.slice(0, insertAt), drag.position, ...points.slice(insertAt)]
}

export function buildContactTypes(
  rope: RopeElement,
  dragMidpointAfterIndex?: number,
  opts?: { startTail?: boolean; endTail?: boolean; startPath?: boolean; endPath?: boolean },
): Array<'pulley' | 'deviation' | 'connector' | 'tail' | 'path' | null> {
  const types: Array<'pulley' | 'deviation' | 'connector' | 'tail' | 'path' | null> = [null]
  for (let i = 0; i < rope.contacts.length; i++) {
    if (dragMidpointAfterIndex !== undefined && i >= dragMidpointAfterIndex) {
      // midpoint drag inserts a ghost point; shift subsequent entries
    }
    types.push(rope.contacts[i].contactType)
  }
  types.push(null)

  if (dragMidpointAfterIndex !== undefined) {
    types.splice(dragMidpointAfterIndex + 1, 0, null)
  }

  if (opts?.startTail) {
    if (rope.contacts.length > 0) types[0] = 'tail'
    types.unshift('tail')
  } else if (opts?.startPath) types[0] = 'path'
  if (opts?.endTail) {
    if (rope.contacts.length > 0) types[types.length - 1] = 'tail'
    types.push('tail')
  } else if (opts?.endPath) types[types.length - 1] = 'path'

  return types
}

export function snapCandidateToContact(candidate: SnapCandidate): RopeContact {
  const contactType = candidate.role === 'pulley-sheave'
    ? 'pulley'
    : candidate.role === 'deviation-contact'
      ? 'deviation'
      : 'connector'

  return {
    elementId: candidate.elementId,
    snapPointId: candidate.snapPointId,
    contactType,
  }
}
